/** @param {string} value */
export const normalizeForSearch = (value) => {
  if (typeof value !== 'string') return ''
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

const DEBOUNCE_MS = 160

/** @type {WeakMap<HTMLElement, { refresh: () => void }>} */
const sectionControllers = new WeakMap()

/**
 * @param {HTMLElement} section
 * @param {string} key
 * @returns {string}
 */
const getDataConfig = (section, key) =>
  section.getAttribute(`data-metaobject-grid-search-${key}`) ?? ''

/**
 * @param {HTMLElement} section
 * @returns {boolean}
 */
export const hasPendingPagination = (section) => {
  const grid = section.querySelector('[data-metaobject-grid-pagination-grid]')
  if (!(grid instanceof HTMLElement)) return false

  const totalPages = Number.parseInt(
    grid.dataset.metaobjectGridPaginationPages || '',
    10
  )

  return (
    totalPages > 1 && section.dataset.metaobjectGridPaginationLoaded !== 'true'
  )
}

/**
 * Highlighted cards follow the active vehicle tab when they declare a token.
 * A card without one stays visible on every tab so an unscoped entry is never
 * silently dropped.
 *
 * @param {HTMLElement} item
 * @param {string} filterAttribute
 * @param {string} normalizedFilter
 * @returns {boolean}
 */
export const featuredItemMatchesVehicleFilter = (
  item,
  filterAttribute,
  normalizedFilter
) => {
  if (!filterAttribute || normalizedFilter.length === 0) return true

  const itemValue = normalizeForSearch(item.getAttribute(filterAttribute) ?? '')
  if (itemValue.length === 0) return true

  return itemValue === normalizedFilter
}

/**
 * @param {HTMLElement} section
 */
const initMetaobjectGridSearch = (section) => {
  if (!(section instanceof HTMLElement)) return

  const existingController = sectionControllers.get(section)
  if (existingController) {
    existingController.refresh()
    return
  }

  const input = section.querySelector('[data-metaobject-grid-search-input]')
  const grid = section.querySelector('[data-metaobject-grid-search-grid]')
  const emptyState = section.querySelector(
    '[data-metaobject-grid-search-empty]'
  )
  const itemSelector =
    getDataConfig(section, 'item-selector') ||
    section.dataset.metaobjectGridSearchItemSelector
  const searchAttribute =
    getDataConfig(section, 'attribute') ||
    section.dataset.metaobjectGridSearchAttribute ||
    'data-search-term'
  const filterAttribute =
    getDataConfig(section, 'filter-attribute') ||
    section.dataset.metaobjectGridSearchFilterAttribute ||
    ''
  const filterButtons = Array.from(
    section.querySelectorAll('[data-metaobject-grid-search-filter-button]')
  ).filter((button) => button instanceof HTMLButtonElement)

  /** Unstick vehicle-filter SSR `data-metaobject-grid-search-ready="false"` if init aborts. */
  const releaseSearchReadyFallback = () => {
    section.dataset.metaobjectGridSearchReady = 'true'
  }

  if (!(input instanceof HTMLInputElement)) {
    releaseSearchReadyFallback()
    return
  }
  if (!(grid instanceof HTMLElement)) {
    releaseSearchReadyFallback()
    return
  }
  if (!(emptyState instanceof HTMLElement)) {
    releaseSearchReadyFallback()
    return
  }
  if (!itemSelector) {
    releaseSearchReadyFallback()
    return
  }

  const collectGridItems = () =>
    Array.from(grid.querySelectorAll(itemSelector)).filter(
      (item) => item instanceof HTMLElement
    )

  /**
   * @param {HTMLElement} item
   * @param {string} normalizedQuery
   * @param {string} normalizedFilter
   * @returns {boolean}
   */
  const itemMatchesFilters = (item, normalizedQuery, normalizedFilter) => {
    const value = normalizeForSearch(item.getAttribute(searchAttribute) ?? '')
    const matchesQuery =
      normalizedQuery.length === 0 || value.includes(normalizedQuery)

    if (!matchesQuery) return false

    if (item.hasAttribute('data-library-highlighted')) {
      return featuredItemMatchesVehicleFilter(
        item,
        filterAttribute,
        normalizedFilter
      )
    }

    const filterValue = filterAttribute
      ? normalizeForSearch(item.getAttribute(filterAttribute) ?? '')
      : ''

    return normalizedFilter.length === 0 || filterValue === normalizedFilter
  }

  let activeFilterValue = ''

  const syncFilterButtons = () => {
    for (const button of filterButtons) {
      const buttonValue = normalizeForSearch(
        button.getAttribute('data-metaobject-grid-search-filter-value') ?? ''
      )
      button.setAttribute(
        'aria-pressed',
        buttonValue === activeFilterValue ? 'true' : 'false'
      )
    }
  }

  const updateResults = () => {
    const normalizedQuery = normalizeForSearch(input.value)
    const normalizedFilter = filterAttribute ? activeFilterValue : ''
    const paginationPending = hasPendingPagination(section)
    const gridItems = collectGridItems()
    let hasVisibleGrid = false

    for (const item of gridItems) {
      const isHighlighted = item.hasAttribute('data-library-highlighted')

      // Grid visibility waits for the pagination merge; highlighted tiles are
      // still filtered so a later slice cannot flash an off-tab card.
      if (paginationPending && !isHighlighted) continue

      const shouldShow = itemMatchesFilters(
        item,
        normalizedQuery,
        normalizedFilter
      )

      item.hidden = !shouldShow

      if (shouldShow) {
        hasVisibleGrid = true
      }
    }

    if (paginationPending) {
      emptyState.hidden = true
      return
    }

    emptyState.hidden = hasVisibleGrid
    grid.hidden = !hasVisibleGrid
  }

  /**
   * @param {string} nextValue
   * @param {boolean} [shouldRefresh]
   */
  const setActiveFilter = (nextValue, shouldRefresh = true) => {
    activeFilterValue = normalizeForSearch(nextValue)
    syncFilterButtons()

    if (shouldRefresh) {
      window.requestAnimationFrame(updateResults)
    }
  }

  /** @type {number | undefined} */
  let debounceTimer

  const handleInput = () => {
    window.clearTimeout(debounceTimer)
    debounceTimer = window.setTimeout(() => {
      window.requestAnimationFrame(updateResults)
    }, DEBOUNCE_MS)
  }

  input.addEventListener('input', handleInput)
  input.addEventListener('search', handleInput)

  if (filterAttribute && filterButtons.length > 0) {
    const pressedFilterValue = filterButtons
      .find((button) => button.getAttribute('aria-pressed') === 'true')
      ?.getAttribute('data-metaobject-grid-search-filter-value')
    const defaultFilterValue =
      pressedFilterValue ??
      (getDataConfig(section, 'default-filter') ||
        section.dataset.metaobjectGridSearchDefaultFilter ||
        filterButtons[0]?.getAttribute(
          'data-metaobject-grid-search-filter-value'
        ) ||
        '')

    for (const button of filterButtons) {
      button.addEventListener('click', () => {
        const nextValue =
          button.getAttribute('data-metaobject-grid-search-filter-value') || ''

        if (normalizeForSearch(nextValue) === activeFilterValue) return
        setActiveFilter(nextValue)
      })
    }

    setActiveFilter(defaultFilterValue, false)
  }

  sectionControllers.set(section, {
    refresh: updateResults
  })
  updateResults()
  section.dataset.metaobjectGridSearchReady = 'true'
}

/**
 * @param {ParentNode} [root]
 */
const initMetaobjectGridSearchSections = (root = document) => {
  const sections = root.querySelectorAll('[data-metaobject-grid-search]')

  for (const section of sections) {
    if (section instanceof HTMLElement) {
      initMetaobjectGridSearch(section)
    }
  }
}

const bootMetaobjectGridSearch = () => {
  initMetaobjectGridSearchSections(document)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootMetaobjectGridSearch)
} else {
  bootMetaobjectGridSearch()
}

document.addEventListener('shopify:section:load', (event) => {
  const detail = event instanceof CustomEvent ? event.detail : undefined
  const sectionId =
    (detail && typeof detail.sectionId === 'string' && detail.sectionId) ||
    (detail && typeof detail.section_id === 'string' && detail.section_id)

  let root = event.target
  if (sectionId) {
    const byId = document.getElementById(`shopify-section-${sectionId}`)
    if (byId instanceof HTMLElement) {
      root = byId
    }
  }

  if (root instanceof HTMLElement) {
    initMetaobjectGridSearchSections(root)
  } else {
    initMetaobjectGridSearchSections(document)
  }
})

document.addEventListener('metaobject-grid:refresh', (event) => {
  const target =
    event.target instanceof HTMLElement
      ? event.target.closest('[data-metaobject-grid-search]')
      : null

  if (target instanceof HTMLElement) {
    initMetaobjectGridSearch(target)
  } else {
    initMetaobjectGridSearchSections(document)
  }
})
